import AdsClick from "@mui/icons-material/AdsClick";
import { MenuItem, Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface ActionFormState {
	actionName: string;
	actionType: string;
}

export interface ActionFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: ActionFormState) => void;
	initialState?: Partial<ActionFormState>;
	isSaving?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
}

const DEFAULT_ACTION = "click";

const ActionForm = (props: ActionFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		getDuplicateNameError,
	} = props;

	const buildInitialState = (): ActionFormState => ({
		actionName: initialState?.actionName ?? "",
		actionType: initialState?.actionType ?? DEFAULT_ACTION,
	});

	const initialFormState = useMemo(buildInitialState, [initialState]);

	const [formState, setFormState] = useState<ActionFormState>(initialFormState);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
		}
	}, [initialFormState, open]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.actionName),
		[getDuplicateNameError, formState.actionName],
	);

	const isSaveDisabled = !formState.actionName.trim() || Boolean(duplicateNameError);

	const handleSave = () => {
		onSave({
			actionName: formState.actionName.trim(),
			actionType: DEFAULT_ACTION,
		});
	};

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Action"
			icon={<AdsClick />}
			iconColor="#3F77E8"
			gradient="linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Action"
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Name"
					value={formState.actionName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							actionName: event.target.value,
						}))
					}
					width="100%"
				/>
				<CanvasFormTextField
					select
					fullWidth
					label="Action"
					value="Click"
					disabled
					width="100%"
				>
					<MenuItem value="Click">Click</MenuItem>
				</CanvasFormTextField>
			</Stack>
		</BaseForm>
	);
};

export default ActionForm;
